import React, { useState, useEffect } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';
import './EmployeeForm.scss';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import translate from "../../../config/translations/translate";
import { 
    getEmployeeById, 
    createEmployee, 
    updateEmployee 
} from '../../../api/employeeApi';
import { setLoading } from '../../../commons/components/application/miscActions';
import * as Yup from 'yup';
import withEventTracking, { trackers } from '../../../config/withEventTracking';
import { setSelectedStoreForReviewLeaderboard } from '../../../commons/components/application/applicationActions';

const MySwal = withReactContent(Swal);

const EmployeeFormContainer = ({t, trackEvent}) => {
    const { id } = useParams();
    const isNewEmployee = !id || id === 'new';
    const selectedCompany = useSelector(state => state.application.selectedCompany);
    const availableStores = useSelector(state => state.application.availableStores || []);
    const [employee, setEmployee] = useState(null);
    const [stores, setStores] = useState([]);
    const selectedStoreForReviewLeaderboard = useSelector(state => state.application.selectedStoreForReviewLeaderboard);

    const queryParams = new URLSearchParams(window.location.search);
    const storeIdFromUrl = queryParams.get('storeId');
    
    useEffect(() => {
        if (!selectedCompany || !selectedCompany.id) return;
        
        const loadData = async () => {
            try {
                const companyStores = availableStores.filter(
                    store => store.companyId === selectedCompany.id
                );
                setStores(companyStores);
                
                if (isNewEmployee) {
                    setEmployee({
                        name: '',
                        lastName: '',
                        username: '',
                        email: '',
                        storeId: storeIdFromUrl ? storeIdFromUrl 
                                : selectedStoreForReviewLeaderboard ? 
                            (selectedStoreForReviewLeaderboard.id ? selectedStoreForReviewLeaderboard.id : selectedStoreForReviewLeaderboard.storeId) 
                            : '',
                        phoneNumber: '',
                        active: true
                    });
                } else {
                    const employeeResponse = await getEmployeeById(id);
                    if (employeeResponse && employeeResponse.data && employeeResponse.data.data) {
                        const employeeData = employeeResponse.data.data;
                        setEmployee({
                            name: employeeData.name || '',
                            lastName: employeeData.lastName || '',
                            username: employeeData.username || '', // Guardamos el username original
                            email: employeeData.email || '',
                            storeId: employeeData.stores && employeeData.stores.length > 0 ? 
                                employeeData.stores[0].id : '',
                            phoneNumber: employeeData.phoneNumber || '',
                            active: employeeData.active,
                            originalUsername: employeeData.username || '' // Guardamos una copia para preservar el valor original
                        });
                    }
                }
            } catch (error) {
                console.error('Error loading data:', error);
                MySwal.fire({
                    icon: 'error',
                    text: t('errorLoadingData'),
                    confirmButtonText: t('close'),
                    confirmButtonColor: '#13589D'
                });
            }
        };
        
        loadData();
    }, [selectedCompany, id, isNewEmployee, availableStores]);
    
    if (employee) {
        return <EmployeeFormComponent 
            t={t}
            employee={employee} 
            stores={stores} 
            isNewEmployee={isNewEmployee} 
            trackEvent={trackEvent}
        />;
    } else {
        return <div className="text-center p-5">
            <div className="spinner-border text-primary" role="status">
                <span className="sr-only">{t('loading')}</span>
            </div>
        </div>;
    }
};

const EmployeeFormComponent = ({ t, employee, stores, isNewEmployee, trackEvent }) => {
    const dispatch = useDispatch();
    const history = useHistory();
    const { id } = useParams();
    const selectedCompany = useSelector(state => state.application.selectedCompany);
        
    const handleSubmit = async (values, { setSubmitting }) => {
        try {
            dispatch(setLoading(true));
            
            if (isNewEmployee) {
                await createEmployee({
                    name: values.name,
                    lastName: values.lastName,
                    username: values.username,
                    email: values.email,
                    storeId: values.storeId,
                    phoneNumber: values.phoneNumber,
                    companyId: selectedCompany.id
                });
                
                MySwal.fire({
                    icon: 'success',
                    text: t('employeeCreatedSuccessfully'),
                    confirmButtonText: t('accept'),
                    confirmButtonColor: '#13589D'
                });

                dispatch(setSelectedStoreForReviewLeaderboard({id: parseInt(values.storeId)}));

            } else {
                await updateEmployee(id, {
                    name: values.name,
                    lastName: values.lastName,
                    username: values.originalUsername, // Usamos el username original, no el del formulario
                    email: values.email,
                    storeId: values.storeId,
                    phoneNumber: values.phoneNumber,
                    active: values.active,
                    companyId: selectedCompany.id
                });
                
                MySwal.fire({
                    icon: 'success',
                    text: t('employeeUpdatedSuccessfully'),
                    confirmButtonText: t('accept'),
                    confirmButtonColor: '#13589D'
                });
            }
            
            history.push('/employee', { reload: true });
        } catch (error) {
            console.error('Error:', error);
            MySwal.fire({
                icon: 'error',
                text: t('errorSavingData'),
                confirmButtonText: t('close'),
                confirmButtonColor: '#13589D'
            });
        } finally {
            setSubmitting(false);
            dispatch(setLoading(false));
        }
    };
    
    return (
        <div className="employee-form-container">
            <h2 className="form-title">
                {isNewEmployee ? t('newEmployee') : t('editEmployee')}
            </h2>
            
            <Formik
                initialValues={employee}
                onSubmit={handleSubmit}
                validationSchema={Yup.object().shape({
                    name: Yup.string().required(t('nameRequired')),
                    lastName: Yup.string().required(t('lastNameRequired')),
                    email: Yup.string().email(t('emailInvalid')).required(t('emailRequired')),
                    phoneNumber: Yup.string().matches(
                        /^[\d\s-()+.]*$/,
                        t('phoneNumberInvalid')
                    ),
                    storeId: Yup.string().required(t('storeRequired'))
                })}
            >
                {({ isSubmitting, values, setFieldValue }) => (
                    <Form>
                        <div className="form-group">
                            <label htmlFor="name">{t('name')}</label>
                            <Field
                                type="text"
                                name="name"
                                id="name"
                                className="form-control"
                            />
                            <ErrorMessage name="name" component="div" className="text-danger" />
                        </div>
                        
                        <div className="form-group">
                            <label htmlFor="lastName">{t('lastName')}</label>
                            <Field
                                type="text"
                                name="lastName"
                                id="lastName"
                                className="form-control"
                                />
                            <ErrorMessage name="lastName" component="div" className="text-danger" />
                        </div>
                        
                        <div className="form-group">
                            <label htmlFor="email">{t('email')}</label>
                            <Field
                                type="email"
                                name="email"
                                id="email"
                                className="form-control"
                            />
                            <ErrorMessage name="email" component="div" className="text-danger" />
                        </div>

                        <div className="form-group">
                            <label htmlFor="phoneNumber">{t('phoneNumber')}</label>
                            <Field
                                type="text"
                                name="phoneNumber"
                                id="phoneNumber"
                                className="form-control"
                                onKeyPress={(e) => {
                                    const allowedChars = /[\d\s-()+.]/;
                                    if (!allowedChars.test(e.key)) {
                                        e.preventDefault();
                                    }
                                }}
                            />
                            <ErrorMessage name="phoneNumber" component="div" className="text-danger" />
                        </div>
                        
                        <div className="form-group">
                            <label htmlFor="storeId">{t('associatedStore')}</label>
                            <select 
                                name="storeId" 
                                id="storeId" 
                                className="form-control"
                                value={values.storeId || ''}
                                onChange={e => setFieldValue("storeId", e.target.value)}
                            >
                                <option value="">{t('selectStore')}</option>
                                {stores.map(store => (
                                    <option key={store.id} value={store.id}>
                                        {store.name}
                                    </option>
                                ))}
                            </select>
                            <ErrorMessage name="storeId" component="div" className="text-danger" />
                        </div>
                        
                        {!isNewEmployee && (
                            <div className="form-check">
                                <Field
                                    type="checkbox"
                                    name="active"
                                    id="active"
                                    className="form-check-input"
                                    checked={values.active}
                                    onChange={e => setFieldValue("active", e.target.checked)}
                                />
                                <label className="form-check-label" htmlFor="active">
                                    {t('activeState')}
                                </label>
                            </div>
                        )}
                        
                        <div className="button-container mt-4">
                            <button 
                                type="button" 
                                className="btn btn-secondary mr-2" 
                                onClick={() => history.push('/employee')}
                            >
                                {t('cancel')}
                            </button>
                            <button 
                                type="submit" 
                                className="btn btn-primary"
                                disabled={isSubmitting}
                                onClick={() => trackEvent('Review_Leaderboard_create_employee', {
                                    employeeId: employee.id,
                                    employeeStoreId: employee.storeId,
                                    employeeCompanyId: selectedCompany.id
                                }, false, [trackers.MIXPANEL, trackers.INTERCOM])}
                            >
                                {t('save')}
                            </button>
                        </div>
                    </Form>
                )}
            </Formik>
        </div>
    );
};

EmployeeFormContainer.translations = {
    employeeCreatedSuccessfully: {
        en: "Employee created successfully",
        es: "Empleado creado exitosamente"
    },
    employeeUpdatedSuccessfully: {
        en: "Employee updated successfully",
        es: "Empleado actualizado exitosamente"
    },
    errorLoadingData: { 
        en: "Error loading data",
        es: "Error al cargar los datos"
    },
    errorSavingData: {
        en: "Error saving data",
        es: "Error al guardar los datos"
    },
    cancel: {
        en: "Cancel",
        es: "Cancelar"
    },
    save: {
        en: "Save",
        es: "Guardar"
    },
    newEmployee: {
        en: "New employee",
        es: "Nuevo empleado"
    },
    editEmployee: {
        en: "Edit employee",
        es: "Editar empleado"
    },
    name: {
        en: "Name",
        es: "Nombre"
    },
    lastName: {
        en: "Last name",
        es: "Apellido"
    },
    username: {
        en: "Username",
        es: "Usuario"
    },
    email: {
        en: "Email",
        es: "Correo"
    },
    phoneNumber: {
        en: "Phone number",
        es: "Número de teléfono"
    },
    associatedStore: {
        en: "Associated store",
        es: "Tienda asociada"
    },
    selectStore: {
        en: "Select store",
        es: "Seleccionar tienda"
    },
    activeState: {
        en: "Active",
        es: "Activo"
    },
    loading: {
        en: "Loading...",
        es: "Cargando..."
    },
    close: {
        en: "Close",
        es: "Cerrar"
    },
    accept: {
        en: "Accept",
        es: "Aceptar"
    },
    nameRequired: {
        en: "Name is required",
        es: "El nombre es requerido"
    },
    lastNameRequired: {
        en: "Last name is required",
        es: "El apellido es requerido"
    },
    usernameRequired: {
        en: "Username is required",
        es: "El usuario es requerido"
    },
    emailInvalid: {
        en: "Invalid email address",
        es: "Dirección de correo electrónico inválida"
    },
    emailRequired: {
        en: "Email is required",
        es: "El correo electrónico es requerido"
    },
    storeRequired: {
        en: "Store is required",
        es: "La tienda es requerida"
    },
    phoneNumberInvalid: {
        en: "Phone number can only contain numbers and symbols: + - ( ) . and spaces",
        es: "El número de teléfono solo puede contener números y símbolos: + - ( ) . y espacios"
    }
}       


export default withEventTracking(translate(EmployeeFormContainer));